/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext } from "react";
import { useQuery, UseQueryResult } from "react-query";
import { TCTeamsFxContext } from "../components/TCTeamsContext";
import {
  getResponseUsingResponseType,
  handleTcTeamsAPIError,
} from "../services/utility";
import { ApiRequest } from "../types/ApiRequest";
import { authentication } from "@microsoft/teams-js";
import { TeamsTeamcenterLogger } from "teamsteamcenterobservability";
import { ErrorMessage } from "../types/CommonTypes";
import { Configuration } from "../services/configuration";
import { Constants } from "../common/constants";
import { TeamsFxContext } from "../components/Context";
import { ConcurrencyLimiterRegistry } from "../services/concurrencyLimiter";

/**
 * allows to call any APIS from the backend, with the authentication bearer token.
 *
 * @param request Api request object
 * @return {UseQueryResult} query result
 */
export const useApi = (
  request: ApiRequest,
  logger: TeamsTeamcenterLogger
): UseQueryResult<any, any> => {
  const tcTeamsContext = useContext(TCTeamsFxContext);
  const teamsContext = useContext(TeamsFxContext);
  let callKey = request.key ? request.key : request.url;

  callKey = callKey instanceof Array ? callKey : [callKey, request.body];

  // calling API
  const queryResult: UseQueryResult<any, Error> = useQuery<any, Error>(
    callKey,
    () => {
      return callApiAsync(request, logger, teamsContext, tcTeamsContext);
    },
    {
      refetchInterval: 0,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: request.enabled,
      retry: false,
      useErrorBoundary: false,
      onSuccess: () => {
        if (request.invalidateQuery) {
          tcTeamsContext?.queryClient?.invalidateQueries({
            predicate: (query: any) => {
              for (const key of query.queryKey as string[]) {
                const invalidate =
                  (request.invalidateQuery && request.invalidateQuery(key)) ||
                  false;
                if (invalidate) return true;
              }
              return false;
            },
          });
        }
      },
    }
  );

  return { ...queryResult };
};

export const callApiAsync = async (
  request: ApiRequest,
  logger: TeamsTeamcenterLogger,
  teamsContext: any,
  tcTeamsContext: any
) => {
  // Use Teams-native SSO token — no hidden iframes needed
  const rawToken = await authentication.getAuthToken();
  if (!rawToken || !request.url || !request.enabled) {
    return null;
  }

  // Get TenantId for logger
  if (
    typeof teamsContext?.userInfo !== "undefined" &&
    teamsContext?.userInfo !== null
  ) {
    const tenantId = teamsContext?.userInfo?.tenantId;
    if (tenantId !== null) {
      logger.setTenantId(tenantId);
    }
  }

  const logContext = logger.createContext({ actionId: request.action });
  logger.logInformation(`Device Type: ${teamsContext?.clientType}`, logContext);
  if (request.action) {
    logger.logInformation(request.action + "() called.", logContext);
  }

  const correlationId = logContext.requestId;
  const teamsLang = Configuration.getValueFromLocalStorage(
    Constants.cacheTeamcenterLanguage
  );
  const teamcenterConfiguration = Configuration.getValueFromLocalStorage(
    Constants.cacheTeamcenterCode
  );
  const selectedSite = Configuration.getValueFromLocalStorage(
    Constants.cacheSelectedSite
  );
  const activeCapability = Configuration.getValueFromLocalStorage(
    Constants.activeCapability
  );
  logger.logInformation(
    `Request Made for Capability - ${activeCapability?.capabilityName}`,
    logContext
  );
  const configuration = Configuration.getValueFromLocalStorage(
    Constants.cacheConfiguration
  );
  const tcVersionParam = localStorage.getItem(Constants.cacheTcVersion);
  // preparing the headers
  const headers = new Headers();
  headers.append("Authorization", `Bearer ${rawToken}`);
  headers.append("Content-Type", "application/json");
  headers.append("Accept", "application/json");
  headers.append(
    "sessionId",
    tcTeamsContext?.teamcenter.session?.sessionId || ""
  );
  headers.append("userId", tcTeamsContext?.teamcenter.session?.userId || "");
  headers.append("x-siemens-session-id", logContext.sessionId);
  headers.append("x-siemens-operation-id", logContext.actionId);
  headers.append("appType", Constants.appType);
  headers.append("userAadOid", teamsContext?.userInfo?.localAccountId || "");
  headers.append("x-correlation-id", correlationId);
  headers.append("locale", teamsLang);
  if (activeCapability && activeCapability.capabilityName) {
    headers.append("capability", activeCapability.capabilityName);
  }
  if (configuration && configuration.tcVersion) {
    headers.append("tcVersion", configuration.tcVersion);
  } else if (tcVersionParam) {
    headers.append("tcVersion", tcVersionParam);
  } else {
    headers.append("tcVersion", "default");
  }
  if (teamcenterConfiguration && teamcenterConfiguration.siteId) {
    headers.append("siteId", teamcenterConfiguration.siteId);
  } else {
    if (selectedSite && selectedSite.siteId) {
      headers.append("siteId", selectedSite.siteId);
    }
  }

  // Check if appDeploymentId is already provided in request headers
  // In case of api calls to MUN broker, app deployment id is provided already in request headers
  const hasAppDeploymentId =
    request.headers?.some(
      ([key, value]) =>
        key.toLowerCase() === "appdeploymentid" &&
        value !== null &&
        value !== undefined &&
        value !== ""
    ) ?? false;

  if (selectedSite && selectedSite.appDeploymentId && !hasAppDeploymentId) {
    headers.append("appDeploymentId", selectedSite.appDeploymentId);
  }

  if (request.headers) {
    request.headers.forEach((header) => {
      headers.append(header[0], header[1]);
    });
  }
  const requestInit: RequestInit = {
    method: request.method ?? "GET",
    headers: headers,
  };

  if (
    request.method &&
    (request.method === "POST" || request.method === "PUT")
  ) {
    requestInit.body = request.body;
  }

  //const response = await fetch(request.url, requestInit);
  var response = new Response();
  try {
    // Only this changes — wrap fetch with the limiter

    const limiter = ConcurrencyLimiterRegistry.getLimiter(request.action);

    // Uncomment the following lines to log the stats of the limiter before and after the request is processed
    // // Log BEFORE entering the queue
    // const statsBefore = limiter.getStats();
    // console.log(
    //   `[Limiter:${request.action}] Queuing request | running: ${statsBefore.running} | waiting: ${statsBefore.queued}`
    // );

    response = await limiter.run(() => fetch(request.url, requestInit));

    // // Log AFTER the slot is released
    // const statsAfter = limiter.getStats();
    // console.log(
    //   `[Limiter:${request.action}] Request done   | running: ${statsAfter.running} | waiting: ${statsAfter.queued}`
    // );
  } catch (error) {
    // Ignore the error here, as it will be handled in the next step when checking the response status code.
  }

  if (request.action && response) {
    logger.logInformation(request.action + "() completed.", logContext);
  }

  if (!response || response.status < 200 || response.status > 204) {
    const errorMessage: ErrorMessage = await handleTcTeamsAPIError(
      request.url,
      correlationId,
      response,
      logContext
    );
    if (response.status === 504)
      errorMessage.message = "The request timed out. Please try again.";
    if (
      response.status === 404 &&
      errorMessage.message === "Success" &&
      request.action.includes("MUN")
    ) {
      // In cases when client passes page number where data is not available, MUN broker might return 404 status code with a message "Success" in the response body, due to which the error is not handled properly. Hence adding an additional check to handle such cases.
      return;
    }
    tcTeamsContext?.failure.setError(errorMessage);
  }

  return await getResponseUsingResponseType(request.returnType, response);
};
